/**
 * Cleaner Detail Drawer (read-only)
 *
 * ESM replacement for the inline <script> that used to live in
 * ``templates/office/components/_cleaner_drawer.html``.
 *
 * Auto-init is wired by adding ``data-auto-init="cleanerDrawer"`` to
 * the drawer root. URL template + translation strings come from
 * ``data-*`` attributes on that same root.
 *
 * Public API (kept identical to the legacy inline script for backward
 * compatibility — pages still call ``window.showCleanerDrawer(id)``):
 *   - ``window.showCleanerDrawer(id)`` opens the drawer for that ID.
 *   - ``window.hideCleanerDrawer()`` closes it.
 *
 * Note: the legacy script ran ``executeNestedScripts`` on the loaded
 * HTML and wired ``.drawer-close`` buttons inside it. Verified empty
 * (templates/office/cleaners/_detail_content.html ships no <script>
 * tag and no .drawer-close class), so this defensive plumbing is
 * dropped here. If a future cleaner detail partial needs nested
 * scripts, pass ``executeNestedScripts: true`` to ``createDynamicDrawer``.
 */

import { createDynamicDrawer } from '../utils/ui/dynamic-drawer.js';

let drawerHelper = null;

export function initCleanerDrawer(options = {}) {
  const {
    drawerUrlTemplate = '/office/cleaners/{id}/',
    defaultTitle = 'Cleaner Details',
    errorMessage = 'Error loading cleaner details',
    closeText = 'Close',
  } = options;

  drawerHelper = createDynamicDrawer({
    drawerId: 'cleanerDetailDrawer',
    backdropId: 'cleanerDrawerBackdrop',
    contentId: 'cleanerDetailDrawerContent',
    titleId: 'cleanerDrawerLabel',
    badgeId: 'cleanerDrawerStatusBadge',
    defaultTitle,
    errorLoadingMessage: errorMessage,
    closeButtonText: closeText,
  });

  if (!drawerHelper) return;

  window.showCleanerDrawer = (cleanerId) => {
    if (!drawerHelper) return;
    const url = drawerUrlTemplate.replace('{id}', encodeURIComponent(cleanerId));
    drawerHelper.open(url);
  };
  window.hideCleanerDrawer = () => {
    if (drawerHelper) drawerHelper.close();
  };
}

/**
 * Auto-init entry point. Called by ``_runAutoInit`` (app.js) on
 * every htmx:afterSwap of #main-content and on htmx:historyRestore.
 */
export function autoInitCleanerDrawer() {
  const root = document.querySelector('[data-auto-init="cleanerDrawer"]');
  if (!root) return;

  initCleanerDrawer({
    drawerUrlTemplate: root.dataset.drawerUrlTemplate || '/office/cleaners/{id}/',
    defaultTitle: root.dataset.defaultTitle || 'Cleaner Details',
    errorMessage: root.dataset.errorMessage || 'Error loading cleaner details',
    closeText: root.dataset.closeText || 'Close',
  });
}
